import React, { useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import NumberFormat, { NumberFormatValues } from "react-number-format";
import { useSelector } from "react-redux";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as MinusButtonSVG } from "../../../../../../../../../../../images/svg/terminals/minus-button.svg";
import { ReactComponent as PlusButtonSVG } from "../../../../../../../../../../../images/svg/terminals/plus-button.svg";
import { TerminalType } from "../../../../../../../../../../../services/Api/Organization/types";
import { getGoAccountsApiURL } from "../../../../../../../../../../../utils/envutil";
import TerminalIcon from "../../../components/TerminalLicenseIcon";
import { TerminalToUpdate, getTerminalTypeTitle } from "../../../utils";

interface Props {
	show: boolean;
	onHide: () => void;
	terminal: TerminalToUpdate;
}

const UpgradeTerminalModal = ({ show, onHide, terminal }: Props) => {
	const { t } = useTranslation();
	const isDangerColor = terminal.propertyAmount > terminal.accessAmount;
	const [value, setValue] = useState<number | "">(terminal.accessAmount);
	const organization = useSelector(selectOrganization);

	const getAddonId = () => {
		switch (terminal.type) {
			case TerminalType.POS:
				return "Addon-GoPOS-Terminal";
			case TerminalType.POS_MOBILE:
				return "Addon-GoPOS-Terminal-Mobile";
			case TerminalType.KDS:
				return "Addon-GoPOS-KDS";
			case TerminalType.KIOSK:
				return "Addon-GoPOS-Kiosk";
			case TerminalType.KDS_NUMBER:
				return "Addon-GoPOS-PickupNumber";
			default:
				return undefined;
		}
	};

	const onSubmit = () => {
		const addonId = getAddonId();
		if (!addonId) return;

		window.open(
			`${getGoAccountsApiURL()}/${organization.company_id}/subscriptions/edit?plan=${
				organization.accesses?.PLAN ?? "plan-gopos-basic"
			}&addon_id=${addonId}&addon_value=${value}`
		);
	};

	const checkIfCanSubtract = () => {
		if (value === "" || value === 0) return false;
		return true;
	};

	const handleSubtract = () => {
		if (checkIfCanSubtract()) {
			setValue((prevState) => (prevState as number) - 1);
		}
	};

	const handleAdd = () => {
		setValue((prevState) => (prevState || 0) + 1);
	};

	const handleOnInputChange = ({ value: inputValue }: NumberFormatValues) => {
		const parsedValue = Number(inputValue);
		if (inputValue === "") {
			setValue(inputValue);
		} else if (parsedValue >= 0) {
			setValue(parsedValue);
		} else {
			setValue(0);
		}
	};

	return (
		<Modal show={show} onHide={onHide} className="upgrade-terminal-modal">
			<Modal.Header closeButton>
				<div className="d-flex align-items-center gap-2">
					<TerminalIcon type={terminal.type} />
					<Modal.Title>{getTerminalTypeTitle(terminal.type, t)}</Modal.Title>
				</div>
			</Modal.Header>
			<Modal.Body className="d-flex flex-column flex-sm-row gap-2">
				<Card>
					<Card.Body className="d-flex flex-column justify-content-center h-100">
						{!terminal.propertyAmount ? (
							<>
								<p className="mb-0 font-weight-bold">{t("modules.terminal.field.no_license.title")}</p>
								<small className="text-muted">{t("modules.terminal.field.enter_quantity.title")}</small>
							</>
						) : (
							<div className="d-flex align-items-center font-weight-bold gap-1">
								<p className="mb-0">{t("modules.terminal.field.used.title")}:</p>
								<p className={`mb-0 ${isDangerColor ? "text-danger" : "text-primary"}`}>
									{terminal.propertyAmount}
								</p>
							</div>
						)}
						{isDangerColor && (
							<small className="text-danger">
								{t("modules.terminal.field.more_licenses_than_bought.title")}
							</small>
						)}
					</Card.Body>
				</Card>
				<Card>
					<Card.Body className="d-flex align-items-center gap-1 justify-content-between w-100 h-100">
						<p className="mb-0 text-muted font-weight-bold me-1">{t("common.word.quantity")}:</p>
						<div className="d-flex align-items-center terminal-amount-options gap-2">
							<MinusButtonSVG
								className={`icon ${checkIfCanSubtract() ? "" : "disabled"}`}
								onClick={handleSubtract}
							/>
							<NumberFormat
								className="form-control"
								value={value}
								onValueChange={handleOnInputChange}
								allowNegative={false}
								decimalScale={0}
							/>
							<PlusButtonSVG className="icon" onClick={handleAdd} />
						</div>
					</Card.Body>
				</Card>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onSubmit} disabled={value === ""}>
					{t("lib:common.action.save")}
				</Button>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default UpgradeTerminalModal;
